import * as React from "react";
import {ChangeEvent, FC, KeyboardEvent, useCallback, useEffect, useRef, useState} from "react";
import {
    Alert,
    alpha,
    Box,
    Button,
    CircularProgress,
    Divider,
    Link,
    Radio,
    Stack,
    Theme,
    ThemeProvider,
    Tooltip,
    useMediaQuery
} from "@mui/material";
import Grid from "@mui/material/Grid";
import {whiteInputsTheme} from "../../../util/theme/whiteInputsTheme";
import CustomDialog from "../../shared/custom-dialog/CustomDialog";
import {RootState, useAppDispatch, useAppSelector} from "../../../store";
import Typography from "@mui/material/Typography";
import {Trans, useTranslation} from "react-i18next";
import {IWish, WishSortOptions, WishStatus, WishType} from "../../../store/wish/Helper";
import ReactGA from "react-ga4";
import {CardGiftcard, CheckCircleOutlined, ErrorOutline, LocalOfferOutlined} from "@mui/icons-material";
import TextField from "@mui/material/TextField";
import {validateEmail} from "../../../util/regex";
import {useTheme} from "@mui/material/styles";
import {formatPrice, getSafeBackendErrorMessage} from "../../../util/utils";
import {toastError} from "../../shared/toast/Toast";
import {startCpayPayment} from "../buy-vouchers-dialog/submitPayment";
import QuestionService, {IQuestion, IQuestionAnswerOption} from "../../../api/questionService";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import WishService from "../../../api/wishService";
import {Link as RouterLink} from "react-router-dom";
import {RoutePaths} from "../../../util/constants";
import {reloadWishAfterAction} from "../../../store/wish/Slice";
import {checkWishToRemove} from "../../../store/wishlist/Slice";
import {getUserDetails} from "../../../store/auth/ActionCreators";
import genie_points from "../../../assets/images/genie-points-short.svg";
import {metaTrack, metaTrackCustom} from "../../../util/metaPixel";
import TranslateSafeLoadingButton from "../../shared/translate-safe-loading-button/TranslateSafeLoadingButton";
import SecurePayments from "../../shared/secure-payments/SecurePayments";
import QtySelector from "../../wishes/display-wishes/common/make-a-wish-grid/qty-selector/QtySelector";
import useWishTicketAvailability from "../../shared/tickets-count-linear-progress/useWishTicketAvailability";
import CheckoutOptionCard from "../../shared/checkout-option-card/CheckoutOptionCard";
import {getWishDetailsPath} from "../../../util/routeHelpers";

interface IProps {
    open: boolean
    handleDialogClose: () => void
    resetTicketCount: () => void
    wish: IWish
    count: number
    orderId: string
    setCount: React.Dispatch<React.SetStateAction<any>>
}

type DialogStep = "question" | "checkout" | "wrong_answer" | "success"
type AnswerFeedback = "idle" | "correct" | "wrong"

const ANSWER_FEEDBACK_DURATION_MS = 1200

const sleep = (ms: number) => new Promise((resolve) => {
    window.setTimeout(resolve, ms)
})

const INITIAL_GIFT_STATE = {
    isGift: false,
    recipient_email: "",
    recipient_name: "",
    personal_message: ""
}

const ObtainEntriesDialog: FC<IProps> = (props) => {
    const {i18n, t} = useTranslation()
    const dispatch = useAppDispatch()
    const theme = useTheme()
    const matchesSm = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))

    const availablePoints = useAppSelector((state: RootState) => state.authReducer.loggedInUser.available_points) ?? 0
    const accessToken = useAppSelector((state: RootState) => state.authReducer.loggedInUser.access_token)
    const {remaining} = useWishTicketAvailability({
        wish: props.wish,
    })
    const bundledWishes = props.wish.type === WishType.BUNDLE ? props.wish.bundled_wishes ?? [] : []
    const effectiveEntryCount = props.count * Math.max(bundledWishes.length, 1)

    const [giftForm, setGiftForm] = useState(INITIAL_GIFT_STATE)
    const [isEmailValid, setIsEmailValid] = useState<boolean>(false)
    const [isNameValid, setIsNameValid] = useState<boolean>(false)

    const [usePoints, setUsePoints] = useState<boolean>(false)
    const [dialogStep, setDialogStep] = useState<DialogStep>("question")
    const [questionLoading, setQuestionLoading] = useState<boolean>(false)
    const [answerLoading, setAnswerLoading] = useState<boolean>(false)
    const [paymentLoading, setPaymentLoading] = useState<boolean>(false)
    const [question, setQuestion] = useState<IQuestion | null>(null)
    const [selectedAnswerId, setSelectedAnswerId] = useState<string>("")
    const [answerFeedback, setAnswerFeedback] = useState<AnswerFeedback>("idle")
    const [successfulTicketCount, setSuccessfulTicketCount] = useState<number>(1)
    const [successWasGift, setSuccessWasGift] = useState<boolean>(false)
    const wasOpenRef = useRef(false)
    const quantityHintTimeoutRef = useRef<number | null>(null)
    const [showQuantityHint, setShowQuantityHint] = useState(false)
    const [bundleSuggestions, setBundleSuggestions] = useState<IWish[]>([])
    const [bundleSuggestionsLoading, setBundleSuggestionsLoading] = useState(false)

    const subtotal = props.wish.cost * props.count
    const isBundle = props.wish.type === WishType.BUNDLE
    const originalUnitCost = props.wish.original_cost ?? 0
    const hasBundleDiscount = isBundle && originalUnitCost > props.wish.cost
    const originalSubtotal = originalUnitCost * props.count
    const bundleSavings = hasBundleDiscount ? originalSubtotal - subtotal : 0
    const bundleDiscountPercent = hasBundleDiscount
        ? Math.floor(((originalUnitCost - props.wish.cost) / originalUnitCost) * 100)
        : 0
    const maxPointsToUse = Math.min(availablePoints, subtotal)
    const canUsePoints = maxPointsToUse > 0
    const appliedPoints = usePoints ? maxPointsToUse : 0
    const total = Math.max(subtotal - appliedPoints, 0)
    const canIncreaseQuantity = remaining > props.count
    const skipPaymentStep = subtotal > 0 && total === 0 && appliedPoints === subtotal
    const steps = skipPaymentStep
        ? [t("question_step"), t("details"), t("confirmation")]
        : [t("question_step"), t("details"), t("payment"), t("confirmation")]

    const getQuestionText = useCallback((item: IQuestion) => {
        if (i18n.language === "mk") {
            return item.question_mk
        }
        if (i18n.language === "al") {
            return item.question_al
        }
        return item.question_en
    }, [i18n.language])

    const getAnswerText = useCallback((item: IQuestionAnswerOption) => {
        if (i18n.language === "mk") {
            return item.answer_mk
        }
        if (i18n.language === "al") {
            return item.answer_al
        }
        return item.answer_en
    }, [i18n.language])

    const getWishName = useCallback((wish: IWish) => (
        wish[`name_${i18n.language}`] || wish.name_en
    ), [i18n.language])

    const loadRandomQuestion = useCallback(async () => {
        setQuestionLoading(true)
        setQuestion(null)
        setAnswerFeedback("idle")
        setSelectedAnswerId("")
        try {
            const {data} = await QuestionService.fetchRandomQuestion()
            setQuestion(data)
        } catch (e: any) {
            toastError(getSafeBackendErrorMessage(e.response?.data?.detail))
        } finally {
            setQuestionLoading(false)
        }
    }, [])

    useEffect(() => {
        if (props.open && !wasOpenRef.current) {
            setDialogStep("question")
            setGiftForm(INITIAL_GIFT_STATE)
            setUsePoints(false)
            setIsEmailValid(true)
            setIsNameValid(true)
            setAnswerFeedback("idle")
            setSelectedAnswerId("")
            setSuccessfulTicketCount(props.count)
            setSuccessWasGift(false)
            void loadRandomQuestion()
        }
        wasOpenRef.current = props.open
    }, [loadRandomQuestion, props.count, props.open])

    useEffect(() => {
        if (!canUsePoints && usePoints) {
            setUsePoints(false)
        }
    }, [canUsePoints, usePoints])

    useEffect(() => {
        const shouldLoadBundleSuggestions = props.open &&
            dialogStep === "checkout" &&
            props.wish.type === WishType.DRAW

        if (!shouldLoadBundleSuggestions) {
            setBundleSuggestions([])
            setBundleSuggestionsLoading(false)
            return
        }

        let isActive = true
        setBundleSuggestionsLoading(true)

        WishService.fetchWishes({
            wish_status: WishStatus.ACTIVE,
            limit: 5,
            offset: 0,
            sort_by: WishSortOptions.COST,
            sort_order: "asc",
            recommended_first: false,
            type: WishType.BUNDLE,
            exclude_free: true,
            included_draw_wish_id: props.wish.id,
        })
            .then((res) => {
                if (isActive) {
                    setBundleSuggestions(res.data.data ?? [])
                }
            })
            .catch(() => {
                if (isActive) {
                    setBundleSuggestions([])
                }
            })
            .finally(() => {
                if (isActive) {
                    setBundleSuggestionsLoading(false)
                }
            })

        return () => {
            isActive = false
        }
    }, [dialogStep, props.open, props.wish.id, props.wish.type])

    useEffect(() => {
        if (!props.open) {
            setShowQuantityHint(false)
        }

        if (props.open && dialogStep === "checkout" && canIncreaseQuantity) {
            setShowQuantityHint(true)
            quantityHintTimeoutRef.current = window.setTimeout(() => {
                setShowQuantityHint(false)
                quantityHintTimeoutRef.current = null
            }, 6000)
        } else if (!props.open || dialogStep !== "checkout" || !canIncreaseQuantity) {
            setShowQuantityHint(false)
        }

        return () => {
            if (quantityHintTimeoutRef.current) {
                window.clearTimeout(quantityHintTimeoutRef.current)
                quantityHintTimeoutRef.current = null
            }
        }
    }, [canIncreaseQuantity, dialogStep, props.open])

    const handleCloseQuantityHint = useCallback(() => {
        if (quantityHintTimeoutRef.current) {
            window.clearTimeout(quantityHintTimeoutRef.current)
            quantityHintTimeoutRef.current = null
        }
        setShowQuantityHint(false)
    }, [])

    const handleVerifyAnswer = async () => {
        if (!question || !selectedAnswerId) {
            return
        }

        setAnswerLoading(true)
        setAnswerFeedback("idle")

        try {
            const {data} = await QuestionService.submitAnswer(question.id, selectedAnswerId)

            const nextFeedback: AnswerFeedback = data.is_correct ? "correct" : "wrong"
            metaTrackCustom("WishQuestionAnswered", {
                content_id: props.wish.id,
                content_name: props.wish.name_en,
                is_correct: data.is_correct,
            });
            setAnswerFeedback(nextFeedback)
            await sleep(ANSWER_FEEDBACK_DURATION_MS)

            if (nextFeedback === "correct") {
                setDialogStep("checkout")
                return
            }
            setDialogStep("wrong_answer")
        } catch (e: any) {
            metaTrackCustom("WishQuestionSubmitFailed", {
                content_id: props.wish.id,
                content_name: props.wish.name_en,
            });
            toastError(getSafeBackendErrorMessage(e.response?.data?.detail))
        } finally {
            setAnswerLoading(false)
        }
    }

    const handleBuyEntries = async () => {
        ReactGA.event({
            category: "Wish",
            action: "make_a_wish_click_confirm",
            label: `Make a wish - Confirm - ${props.wish.id}`
        })

        const {isGift, recipient_name, recipient_email, personal_message} = giftForm
        setPaymentLoading(true)
        try {
            const paymentSession = await startCpayPayment(
                props.orderId,
                props.wish.id,
                props.count,
                isGift
                    ? {
                        recipient_email,
                        recipient_name,
                        personal_message
                    }
                    : {},
                appliedPoints > 0 ? appliedPoints : undefined
            )

            if (paymentSession.requiresPayment) {
                return
            }

            const wishResponse = await WishService.fetchWishDetails(props.wish.id)
            dispatch(reloadWishAfterAction(wishResponse.data))
            dispatch(checkWishToRemove(wishResponse.data))
            if (accessToken) {
                await dispatch(getUserDetails({access_token: accessToken, token_type: "bearer"}))
            }
            metaTrack("Purchase", {
                content_ids: [props.wish.id],
                content_name: props.wish.name_en,
                content_type: props.wish.type,
                currency: "MKD",
                value: total,
                num_items: props.count,
            });
            setSuccessfulTicketCount(effectiveEntryCount)
            setSuccessWasGift(isGift)
            setDialogStep("success")
        } catch (e: any) {
            metaTrackCustom("PurchaseAttemptFailed", {
                content_id: props.wish.id,
                content_name: props.wish.name_en,
                content_type: props.wish.type,
                points_applied: appliedPoints,
                total_due: total,
            });
            if (appliedPoints > 0 && accessToken) {
                // Refresh the user so expired points clamp the form and totals immediately.
                await dispatch(getUserDetails({access_token: accessToken, token_type: "bearer"}))
            }
            toastError(getSafeBackendErrorMessage(e.response?.data?.detail))
        } finally {
            setPaymentLoading(false)
        }
    }

    const handleSuccessClose = () => {
        props.resetTicketCount()
        props.handleDialogClose()
    }

    const handleOnInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.name === "recipient_email" && validateEmail(e.target.value)) {
            setIsEmailValid(true)
        }
        if (e.target.name === "recipient_name" && e.target.value.length > 0) {
            setIsNameValid(true)
        }
        setGiftForm((oldForm) => ({
            ...oldForm,
            [e.target.name]: e.target.value
        }))
    }

    const handleChangeIsGift = (checked?: boolean) => {
        setGiftForm((oldForm) => ({
            ...oldForm,
            isGift: checked ?? !oldForm.isGift,
            recipient_email: "",
            recipient_name: "",
            personal_message: ""
        }))
        setIsEmailValid(true)
        setIsNameValid(true)
    }

    const handleChangeUsePoints = (checked: boolean) => {
        setUsePoints(checked)
    }

    const handleOnBlurEmailField = () => {
        setIsEmailValid(validateEmail(giftForm.recipient_email ?? ""))
    }

    const handleOnBlurNameField = () => {
        setIsNameValid(giftForm.recipient_name.length > 0)
    }

    const isGiftFormValid = !giftForm.isGift ||
        (validateEmail(giftForm.recipient_email) && giftForm.recipient_name.length > 0)

    const isQuestionLocked = questionLoading || answerLoading || answerFeedback !== "idle"
    const getBundleDrawCount = (bundle: IWish) => (
        bundle.bundled_wishes?.length ?? bundle.bundle_wish_ids?.length ?? 0
    )

    const getBundleDiscountPercent = (bundle: IWish) => (
        bundle.original_cost && bundle.original_cost > bundle.cost
            ? Math.floor(((bundle.original_cost - bundle.cost) / bundle.original_cost) * 100)
            : 0
    )

    const visibleBundleSuggestions = bundleSuggestions.slice(0, 2)
    const extraBundleSuggestionsCount = Math.max(bundleSuggestions.length - visibleBundleSuggestions.length, 0)

    const handleSelectAnswer = (answerId: string) => {
        if (isQuestionLocked) {
            return
        }
        setSelectedAnswerId(answerId)
    }

    const handleSelectAnswerByKeyboard = (event: KeyboardEvent<HTMLDivElement>, answerId: string) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault()
            handleSelectAnswer(answerId)
        }
    }

    const getAnswerCardSx = (answerId: string) => {
        const selected = selectedAnswerId === answerId

        if (!selected) {
            return {
                border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                backgroundColor: alpha(theme.palette.primary.main, 0.03)
            }
        }

        if (answerFeedback === "correct") {
            return {
                border: `1px solid ${alpha(theme.palette.success.main, 0.6)}`,
                backgroundColor: alpha(theme.palette.success.main, 0.18)
            }
        }

        if (answerFeedback === "wrong") {
            return {
                border: `1px solid ${alpha(theme.palette.error.main, 0.6)}`,
                backgroundColor: alpha(theme.palette.error.main, 0.18)
            }
        }

        return {
            border: `1px solid ${alpha(theme.palette.primary.main, 0.55)}`,
            backgroundColor: alpha(theme.palette.primary.main, 0.1)
        }
    }

    const questionStepContent = (
        <Stack spacing={2} mt={2}>
            <Alert severity="info" sx={{alignItems: "center"}}>
                <Typography variant="body2" fontWeight={600}>
                    {t("answer_question_to_continue")}
                </Typography>
            </Alert>
            <Box
                p={{xs: 1.5, sm: 2}}
                border={`1px solid ${alpha(theme.palette.primary.main, 0.35)}`}
                borderRadius={1}
                bgcolor={alpha(theme.palette.primary.main, 0.04)}
                sx={{
                    backgroundImage: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.03)} 0%, transparent 100%)`
                }}
            >
                {questionLoading ? (
                    <Grid container justifyContent="center" py={2}>
                        <CircularProgress size={30} color="primary"/>
                    </Grid>
                ) : question ? (
                    <Stack spacing={1.25}>
                        <Typography
                            variant="h6"
                            color="primary"
                            fontWeight={700}
                            sx={{fontSize: {xs: "1.15rem", sm: "1.35rem"}, mb: '0.5em !important'}}
                        >
                            {getQuestionText(question)}
                        </Typography>

                        {question.answers.map((answer) => (
                            <Box
                                key={answer.id}
                                p={{xs: 0.95, sm: 1.15}}
                                borderRadius={1}
                                role="button"
                                tabIndex={0}
                                onClick={() => handleSelectAnswer(answer.id)}
                                onKeyDown={(event) => handleSelectAnswerByKeyboard(event, answer.id)}
                                sx={{
                                    ...getAnswerCardSx(answer.id),
                                    cursor: isQuestionLocked ? "default" : "pointer",
                                    transition: "all 0.18s ease",
                                    "&:hover": {
                                        borderColor: isQuestionLocked
                                            ? undefined
                                            : alpha(theme.palette.primary.main, 0.55),
                                        transform: isQuestionLocked ? "none" : "translateY(-1px)",
                                        boxShadow: isQuestionLocked ? "none" : `0 3px 10px ${alpha(theme.palette.primary.main, 0.08)}`
                                    }
                                }}
                            >
                                <Grid container alignItems="center" wrap="nowrap" columnGap={1.25}>
                                    <Radio
                                        checked={selectedAnswerId === answer.id}
                                        sx={{
                                            p: 0.5,
                                            color: theme.palette.primary.main,
                                            "&.Mui-checked": {color: theme.palette.primary.main}
                                        }}
                                        disableRipple
                                    />
                                    <Typography
                                        variant="body1"
                                        color="primary"
                                        sx={{fontSize: {xs: "0.95rem", sm: "1.05rem"}, lineHeight: 1.25}}
                                    >
                                        {getAnswerText(answer)}
                                    </Typography>
                                </Grid>
                            </Box>
                        ))}
                    </Stack>
                ) : (
                    <Alert severity="error">{t("question_unavailable")}</Alert>
                )}
            </Box>
        </Stack>
    )

    const giftOptionContent = (
        <CheckoutOptionCard
            selected={giftForm.isGift}
            onChange={handleChangeIsGift}
            icon={<CardGiftcard sx={{fontSize: {xs: 22, sm: 24}}}/>}
            title={t("send_as_gift")}
            description={t("send_entry_as_gift_tooltip")}
        >
            {giftForm.isGift && (
                <Stack spacing={2}>
                    <TextField
                        required
                        color="primary"
                        type="email"
                        fullWidth
                        autoFocus={giftForm.isGift}
                        name="recipient_email"
                        value={giftForm.recipient_email}
                        onChange={handleOnInputChange}
                        label={t("recipient_email")}
                        error={!isEmailValid}
                        helperText={!isEmailValid && t("email_field_not_valid")}
                        onBlur={handleOnBlurEmailField}
                    />

                    <TextField
                        color="primary"
                        type="text"
                        required
                        fullWidth
                        name="recipient_name"
                        value={giftForm.recipient_name}
                        onChange={handleOnInputChange}
                        label={t("recipient_name")}
                        error={!isNameValid}
                        helperText={!isNameValid && t("field_required")}
                        onBlur={handleOnBlurNameField}
                    />

                    <TextField fullWidth
                               color="primary"
                               value={giftForm.personal_message}
                               name="personal_message"
                               onChange={handleOnInputChange}
                               multiline
                               rows={3}
                               label={t("personalized_message")}/>
                </Stack>
            )}
        </CheckoutOptionCard>
    )

    const bundleSuggestionContent = props.wish.type === WishType.DRAW && (bundleSuggestionsLoading || visibleBundleSuggestions.length > 0) ? (
        <Box
            mt={2}
            p={{xs: 1.45, sm: 1.65}}
            border={`1px dashed ${alpha(theme.palette.primary.main, 0.28)}`}
            borderRadius={1}
            sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.02)
            }}
        >
            {bundleSuggestionsLoading ? (
                <Grid container alignItems="center" columnGap={1.25}>
                    <CircularProgress size={18} color="primary"/>
                    <Typography variant="body2" color="primary" fontWeight={600}>
                        {t("bundle_checkout_suggestion_loading")}
                    </Typography>
                </Grid>
            ) : (
                <Stack spacing={1.1}>
                    <Grid container columnGap={1.25} wrap="nowrap">
                        <Box
                            sx={{
                                width: 38,
                                height: 38,
                                borderRadius: "50%",
                                display: "grid",
                                placeItems: "center",
                                flexShrink: 0,
                                color: theme.palette.primary.main,
                                backgroundColor: alpha(theme.palette.primary.main, 0.08)
                            }}
                        >
                            <LocalOfferOutlined sx={{fontSize: 21}}/>
                        </Box>
                        <Box flex={1} minWidth={0}>
                            <Typography variant="body1" color="primary" fontWeight={800} lineHeight={1.2}>
                                {t("bundle_checkout_suggestion_title")}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{mt: 0.35, color: alpha(theme.palette.primary.main, 0.76), lineHeight: 1.35}}
                            >
                                {t("bundle_checkout_suggestion_description")}
                            </Typography>
                        </Box>
                    </Grid>

                    <Stack spacing={0.7}>
                        {visibleBundleSuggestions.map((bundle) => {
                            const discountPercent = getBundleDiscountPercent(bundle)
                            const additionalDrawCount = Math.max(getBundleDrawCount(bundle) - 1, 0)
                            const hasDiscount = discountPercent > 0

                            return (
                                <Box
                                    key={bundle.id}
                                    p={{xs: 0.95, sm: 1.05}}
                                    borderRadius={1}
                                    sx={{
                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                        backgroundColor: alpha(theme.palette.primary.main, 0.025)
                                    }}
                                >
                                    <Grid container alignItems="center" columnGap={1} rowGap={0.85} wrap="nowrap"
                                          sx={{flexWrap: {xs: "wrap", sm: "nowrap"}}}>
                                        <Box flex={1} minWidth={0}>
                                            <Grid container alignItems="center" columnGap={0.65} rowGap={0.3}>
                                                <Typography variant="body2" color="primary" fontWeight={800}>
                                                    {getWishName(bundle)}
                                                </Typography>
                                                {hasDiscount && (
                                                    <Box
                                                        px={0.65}
                                                        py={0.18}
                                                        borderRadius="999px"
                                                        sx={{
                                                            color: theme.palette.tertiary.contrastText,
                                                            backgroundColor: theme.palette.tertiary.main,
                                                            lineHeight: 1,
                                                        }}
                                                    >
                                                        <Typography variant="caption" fontWeight={800}>
                                                            {t("save_percent", {percent: discountPercent})}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Grid>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    display: "block",
                                                    color: alpha(theme.palette.primary.main, 0.68),
                                                    mt: 0.25
                                                }}
                                            >
                                                {t("bundle_checkout_includes_this_draw", {count: additionalDrawCount})}
                                            </Typography>
                                        </Box>

                                        <Grid container alignItems="center" columnGap={1} wrap="nowrap"
                                              sx={{
                                                  width: {xs: "100%", sm: "auto"},
                                                  justifyContent: {xs: "space-between", sm: "flex-end"}
                                              }}>
                                            <Box textAlign={{xs: "left", sm: "right"}} whiteSpace="nowrap">
                                                {hasDiscount && (
                                                    <Typography
                                                        variant="caption"
                                                        color="primary"
                                                        sx={{
                                                            display: "block",
                                                            textDecoration: "line-through",
                                                            opacity: 0.55,
                                                            lineHeight: 1
                                                        }}
                                                    >
                                                        {formatPrice(bundle.original_cost!)}
                                                    </Typography>
                                                )}
                                                <Typography variant="body2" color="primary" fontWeight={800}>
                                                    {formatPrice(bundle.cost)}
                                                </Typography>
                                            </Box>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="primary"
                                                component={RouterLink}
                                                to={getWishDetailsPath(bundle)}
                                                onClick={props.handleDialogClose}
                                                sx={{
                                                    whiteSpace: "nowrap",
                                                    minWidth: "auto",
                                                    px: 1.25,
                                                    py: 0.45,
                                                    borderColor: alpha(theme.palette.primary.main, 0.45),
                                                    color: alpha(theme.palette.primary.main, 0.92),
                                                    "&:hover": {
                                                        borderColor: theme.palette.primary.main,
                                                        backgroundColor: alpha(theme.palette.primary.main, 0.06),
                                                    }
                                                }}
                                            >
                                                {t("view_bundle")}
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )
                        })}
                    </Stack>

                    {extraBundleSuggestionsCount > 0 && (
                        <Typography variant="caption"
                                    sx={{color: alpha(theme.palette.primary.main, 0.62), pl: {xs: 0, sm: 6.3}}}>
                            {t("bundle_checkout_more_options", {count: extraBundleSuggestionsCount})}
                        </Typography>
                    )}
                </Stack>
            )}
        </Box>
    ) : null

    const paymentStepContent = (
        <Box my={2}>
            <Box
                p={2}
                mt={2}
                border={`1px solid ${theme.palette.primary.main}`}
                borderRadius={1}
                sx={{
                    backgroundImage: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, transparent 100%)`
                }}
            >
                <Stack divider={<Divider sx={{backgroundColor: alpha(theme.palette.primary.main, 0.7)}}/>}>
                    <Grid container justifyContent="space-between" alignItems="center" pb={0.5} gap="1em"
                          flexWrap="nowrap">
                        <Typography variant="subtitle2" color="primary">
                            {props.count} x {props.wish[`name_${i18n.language}`]}
                        </Typography>
                        <Box textAlign="right" whiteSpace="nowrap">
                            {hasBundleDiscount && (
                                <Typography
                                    variant="caption"
                                    color="primary"
                                    sx={{
                                        display: "block",
                                        textDecoration: "line-through",
                                        opacity: 0.6,
                                        lineHeight: 1,
                                    }}
                                >
                                    {formatPrice(originalSubtotal)}
                                </Typography>
                            )}
                            <Typography variant="subtitle2" color="primary" fontWeight={600}>
                                {formatPrice(subtotal)}
                            </Typography>
                        </Box>
                    </Grid>
                    {bundledWishes.length > 0 && (
                        <Box py={0.5}>
                            <Typography variant="subtitle2" color="primary" fontWeight={600} mb={0.5}>
                                {t("included_draws")}
                            </Typography>
                            <Stack spacing={0.35}>
                                {bundledWishes.map((wish) => (
                                    <Typography
                                        key={wish.id}
                                        variant="body2"
                                        sx={{color: alpha(theme.palette.primary.main, 0.7)}}
                                    >
                                        {props.count} x {wish[`name_${i18n.language}`]}
                                    </Typography>
                                ))}
                            </Stack>
                        </Box>
                    )}
                    {hasBundleDiscount && (
                        <Grid container justifyContent="space-between" alignItems="center" py={0.5} gap="1em"
                              flexWrap="nowrap">
                            <Typography variant="subtitle2" color="primary">
                                {t("you_save")}
                            </Typography>
                            <Typography variant="subtitle2" color="primary" fontWeight={600} whiteSpace="nowrap">
                                {formatPrice(bundleSavings)} ({bundleDiscountPercent}%)
                            </Typography>
                        </Grid>
                    )}
                    {usePoints && appliedPoints > 0 && (
                        <Grid container justifyContent="space-between" alignItems="center" py={0.5} gap="1em"
                              flexWrap="nowrap">
                            <Typography variant="subtitle2" color="primary">
                                {t("points_discount")}
                            </Typography>
                            <Typography variant="subtitle2" color="primary" fontWeight={600} whiteSpace="nowrap">
                                -{formatPrice(appliedPoints)}
                            </Typography>
                        </Grid>
                    )}
                    <Grid container justifyContent="space-between" alignItems="center" pt={0.5} gap="1em"
                          flexWrap="nowrap">
                        <Typography variant="subtitle1" color="primary" fontWeight={600}>
                            {t("total")}
                        </Typography>
                        <Typography variant="subtitle1" color="primary" fontWeight="bold" whiteSpace="nowrap">
                            {formatPrice(total)}
                        </Typography>
                    </Grid>
                </Stack>
            </Box>

            {bundleSuggestionContent}

            {giftOptionContent}

            {canUsePoints && (
                <CheckoutOptionCard
                    selected={usePoints}
                    onChange={handleChangeUsePoints}
                    icon={<Box component="img" src={genie_points} alt="Genie points" sx={{height: 18, width: "auto"}}/>}
                    title={t("use_genie_points")}
                    description={t("use_genie_points_helper", {count: availablePoints})}
                />
            )}

            <Divider sx={{backgroundColor: theme.palette.primary.main, mt: 1.25}}/>
            <Typography variant="body2" color="primary" mt={0.6}>
                <Trans i18nKey="obtain_entries_notice"
                       components={[<Link
                           sx={{cursor: "pointer"}}
                           color="primary"
                           component={RouterLink}
                           to={RoutePaths.TERMS}
                       />]}/>
            </Typography>
        </Box>
    )

    const wrongAnswerStepContent = (
        <Box textAlign="center" py={2}>
            <ErrorOutline color="primary" sx={{fontSize: "6rem"}}/>
            <Typography variant="h5" color="primary" fontWeight={600}>
                {t("question_answer_wrong_title")}
            </Typography>
            <Typography variant="subtitle1" color="primary">
                {t("question_answer_wrong_message")}
            </Typography>
            <Typography variant="body2" sx={{color: alpha(theme.palette.primary.main, 0.6)}}>
                {t("question_answer_wrong_hint")}
            </Typography>
        </Box>
    )

    const successStepContent = (
        <Box textAlign="center" py={2}>
            <CheckCircleOutlined color="primary" sx={{fontSize: "6rem"}}/>
            <Typography variant="h5" color="primary" fontWeight={600}>
                {t("confirmation")}
            </Typography>
            <Typography variant="subtitle1" color="primary">
                {successWasGift
                    ? t("success_gift_obtain_entries", {count: successfulTicketCount})
                    : t("success_obtain_entries", {count: successfulTicketCount})}
            </Typography>
            <Typography variant="body2" sx={{color: alpha(theme.palette.primary.main, 0.6)}}>
                {t("continue_browsing")}
            </Typography>
        </Box>
    )

    const activeStep = dialogStep === "question" || dialogStep === "wrong_answer"
        ? 0
        : dialogStep === "checkout"
            ? 1
            : (skipPaymentStep ? 2 : 3)

    const dialogContent = (
        <Box>
            <Stepper variant="outlined" activeStep={activeStep} alternativeLabel sx={{mb: 4}}>
                {steps.map((label, idx) => (
                    <Step key={label}>
                        <StepLabel error={dialogStep === "wrong_answer" && idx === 0}>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>
            {dialogStep === "question" && questionStepContent}
            {dialogStep === "checkout" && paymentStepContent}
            {dialogStep === "wrong_answer" && wrongAnswerStepContent}
            {dialogStep === "success" && successStepContent}
        </Box>
    )

    const dialogActions = dialogStep === "question" ? (
        <Grid container justifyContent="flex-end" alignItems="center">
            <TranslateSafeLoadingButton
                variant="contained"
                size="large"
                loading={answerLoading}
                onClick={handleVerifyAnswer}
                disabled={!question || !selectedAnswerId || questionLoading || answerFeedback !== "idle"}
            >
                {t("confirm")}
            </TranslateSafeLoadingButton>
        </Grid>
    ) : dialogStep === "checkout" ? (
        <Stack width="100%" spacing={1.1}>
            <Grid container justifyContent="space-between" alignItems="center" rowGap={1}>
                <Tooltip
                    open={showQuantityHint}
                    arrow
                    placement="top"
                    title={t("obtain_more_entries_hint")}
                    disableFocusListener
                    disableHoverListener
                    disableTouchListener
                >
                    <Box onPointerDownCapture={handleCloseQuantityHint}
                         onKeyDownCapture={handleCloseQuantityHint}>
                        <QtySelector qty={props.count} setQty={props.setCount}
                                     max={remaining}
                                     recommended={false} size="medium"/>
                    </Box>
                </Tooltip>
                <TranslateSafeLoadingButton
                    variant="contained"
                    size="large"
                    loading={paymentLoading}
                    onClick={handleBuyEntries}
                    disabled={!isGiftFormValid}
                >
                    {skipPaymentStep
                        ? t("continue")
                        : matchesSm
                            ? t("proceed_to_payment_short")
                            : t("proceed_to_payment")}
                </TranslateSafeLoadingButton>
            </Grid>
            <SecurePayments
                variant="dialog"
                align="center"
                sx={{width: "100%"}}
            />
        </Stack>
    ) : dialogStep === "success" ? (
        <Grid container justifyContent="flex-end" alignItems="center">
            <TranslateSafeLoadingButton variant="contained" onClick={handleSuccessClose} size="large">
                {t("close")}
            </TranslateSafeLoadingButton>
        </Grid>
    ) : (
        <Grid container justifyContent="flex-end" alignItems="center">
            <TranslateSafeLoadingButton variant="contained" onClick={props.handleDialogClose} size="large">
                {t("close")}
            </TranslateSafeLoadingButton>
        </Grid>
    )

    return (
        <ThemeProvider theme={whiteInputsTheme}>
            <CustomDialog open={props.open}
                          title={t("make_a_wish")}
                          maxWidth="sm"
                          actions={dialogActions}
                          handleDialogClose={dialogStep === "success" ? handleSuccessClose : props.handleDialogClose}
                          content={dialogContent}/>
        </ThemeProvider>
    )
}

export default ObtainEntriesDialog
